/**
 * Carrossel do banner principal (home) — desktop e mobile.
 */
class BelabellyHomeHeroCarousel extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;

    this.viewport = this.querySelector('[data-hero-viewport]');
    this.track = this.querySelector('[data-hero-track]');
    this.slides = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-hero-slide]')]);
    if (!this.viewport || !this.track || this.slides.length === 0) return;

    this._initialized = true;
    this.activeIndex = 0;
    this._timer = null;
    this._reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.autoplayEnabled =
      this.dataset.autoplay === 'true' && this.slides.length > 1 && !this._reducedMotion;
    this.intervalMs = Math.max(3000, (parseInt(this.dataset.interval, 10) || 5) * 1000);

    this.prevBtn = this.querySelector('[data-hero-prev]');
    this.nextBtn = this.querySelector('[data-hero-next]');
    this.dots = /** @type {HTMLButtonElement[]} */ ([...this.querySelectorAll('[data-hero-dot]')]);

    this.prevBtn?.addEventListener('click', () => this.go(-1));
    this.nextBtn?.addEventListener('click', () => this.go(1));
    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this.setActive(i, true));
    });

    this._touch = { startX: 0, moved: false };
    this.viewport.addEventListener(
      'touchstart',
      (e) => {
        this._touch.startX = e.touches[0]?.clientX ?? 0;
        this._touch.moved = false;
      },
      { passive: true }
    );
    this.viewport.addEventListener(
      'touchend',
      (e) => {
        if (this.slides.length < 2) return;
        const endX = e.changedTouches[0]?.clientX ?? 0;
        const delta = endX - this._touch.startX;
        if (Math.abs(delta) < 40) return;
        this._touch.moved = true;
        this.go(delta < 0 ? 1 : -1, true);
      },
      { passive: true }
    );

    this.addEventListener('mouseenter', () => this.pause());
    this.addEventListener('mouseleave', () => this.resume());
    this.addEventListener('focusin', () => this.pause());
    this.addEventListener('focusout', () => this.resume());

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.pause();
      else this.resume();
    });

    this.setActive(0, false);
    this._syncVideos();
    this.resume();
  }

  disconnectedCallback() {
    this.pause();
  }

  go(direction, user = false) {
    const next = (this.activeIndex + direction + this.slides.length) % this.slides.length;
    this.setActive(next, user);
  }

  setActive(index, user = false) {
    if (index < 0 || index >= this.slides.length) return;
    this.activeIndex = index;

    this.slides.forEach((slide, i) => {
      const active = i === index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      if (active) {
        slide.removeAttribute('tabindex');
      } else {
        slide.setAttribute('tabindex', '-1');
      }
    });

    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
      dot.setAttribute('aria-selected', i === index ? 'true' : 'false');
    });

    this.dataset.activeIndex = String(index);
    this._syncVideos();

    if (user) {
      this.pause();
      this.resume();
    }
  }

  _syncVideos() {
    this.querySelectorAll('video').forEach((video) => {
      video.muted = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      const slide = video.closest('[data-hero-slide]');
      const active = !slide || slide.classList.contains('is-active');
      if (active) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }

  pause() {
    if (this._timer) {
      clearInterval(this._timer);
      this._timer = null;
    }
  }

  resume() {
    this.pause();
    if (!this.autoplayEnabled) return;
    this._timer = window.setInterval(() => this.go(1), this.intervalMs);
  }
}

if (!customElements.get('belabelly-home-hero-carousel')) {
  customElements.define('belabelly-home-hero-carousel', BelabellyHomeHeroCarousel);
}
